import { z } from "zod";
import { GoalConfigSchema } from "./config";
import { PlanSchema } from "./plan";

/**
 * The kinds of run the agent does. Each kind says which tools it always has (its core kit), which tool
 * call ends it (the run stops and waits for the owner), and its starting cap. Everything else a run can
 * use comes from the skills it loads (their allowed tools), so the kit never drifts from the skills.
 */
export interface RunKindDef {
  label: string;
  /** The library method this kind of run follows (its latest version unless the goal pins one). */
  method: string;
  /** What the run is asked to do, in plain words. {message} is the owner's message for a chat run. */
  task: string;
  core: string[];
  endsWith: string[];
  capMicros: number;
}

export const RUN_KINDS = {
  learn: {
    label: "Learn the business",
    method: "product-research",
    task: "Learn this business: what it sells, to whom, and how it markets today. Load the {method} skill and follow it.",
    core: ["web_search", "read_page", "find_tool", "call_catalog_tool", "confirm_identity", "save_product"],
    endsWith: ["confirm_identity", "save_product"],
    capMicros: 500_000,
  },
  playbook: {
    label: "Write a playbook",
    method: "playbook-draft",
    task: "Write a playbook for this product and goal. Load the {method} skill and follow it.",
    core: ["read_page", "list_lessons", "save_playbook"],
    endsWith: ["save_playbook"],
    capMicros: 300_000,
  },
  plan: {
    label: "Write the plan",
    method: "goal-planning",
    task: "Write the plan for this goal. Load the {method} skill and follow it. Nothing runs until the owner approves it.",
    core: ["find_tool", "call_catalog_tool", "web_search", "read_page", "list_lessons", "find_targeting", "estimate_reach", "list_events", "save_plan"],
    endsWith: ["save_plan"],
    capMicros: 500_000,
  },
  review: {
    label: "Morning review",
    method: "daily-review",
    task: "Do this morning's review of the goal. Load the {method} skill and follow it.",
    core: ["goal_state", "results_by_ad", "results_breakdown", "list_events", "list_lessons", "propose_action", "propose_change", "propose_lesson", "save_review"],
    endsWith: ["save_review"],
    capMicros: 500_000,
  },
  chat: {
    label: "Answer in the chat",
    method: "goal-chat",
    task: "The owner wrote in this goal's chat: {message}\nAnswer from the goal's own data. Load the {method} skill and follow it.",
    core: ["goal_state", "results_by_ad", "list_leads", "find_targeting", "estimate_reach", "propose_change", "answer"],
    endsWith: ["answer"],
    capMicros: 200_000,
  },
  reply: {
    label: "Draft a reply",
    method: "reply-draft",
    task: "Someone replied to a cold email. Draft the answer for the owner to approve. Load the {method} skill and follow it.",
    core: ["goal_state", "propose_action"],
    endsWith: ["propose_action"],
    capMicros: 100_000,
  },
  ads: {
    label: "Draft the ads",
    method: "ad-drafting",
    task: "Draft the words for this goal's ads: each idea that has no words yet. Load the {method} skill and follow it.",
    core: ["goal_state", "read_page", "save_ads"],
    endsWith: ["save_ads"],
    capMicros: 200_000,
  },
  emails: {
    label: "Write the cold emails",
    method: "email-drafting",
    task: "Write the cold emails this goal sends, as its sender. Load the {method} skill and follow it.",
    core: ["goal_state", "list_leads", "save_emails"],
    endsWith: ["save_emails"],
    capMicros: 200_000,
  },
  list: {
    label: "Build a cold email list",
    method: "list-building",
    task: "Build the cold email list for this market: {message}. Load the {method} skill and follow it.",
    core: ["web_search", "read_page", "find_tool", "call_catalog_tool", "modelmatch_lookup", "linkedin_search_posts", "linkedin_profile", "save_lead", "list_leads", "finish_list"],
    endsWith: ["finish_list"],
    capMicros: 1_000_000,
  },
  wrapup: {
    label: "Wrap up the goal",
    method: "wrap-up",
    task: "The goal has ended. Write its wrap-up and the lessons it leaves. Load the {method} skill and follow it.",
    core: ["goal_state", "results_by_ad", "list_lessons", "propose_lesson", "save_wrapup"],
    endsWith: ["save_wrapup"],
    capMicros: 300_000,
  },
} satisfies Record<string, RunKindDef>;

export type RunKind = keyof typeof RUN_KINDS;

/** The model a run uses when neither the goal nor the account names one. */
export const DEFAULT_MODEL = "deepseek/deepseek-v4.1-flash";

/** Calls per run for tools that cost money or take long; a goal's kit carries these. */
export const DEFAULT_TOOL_LIMITS: Record<string, number> = {
  web_search: 15,
  read_page: 12,
  call_catalog_tool: 8,
  linkedin_search_posts: 4,
};
export const isRunKind = (k: string): k is RunKind => Object.hasOwn(RUN_KINDS, k);

export const RunStateSchema = z.enum(["queued", "running", "waiting", "done", "failed", "cancelled", "interrupted"]);
export type RunState = z.infer<typeof RunStateSchema>;

/** A library item exactly as the run loads it: the pinned version with the goal's edits applied. */
export const ManifestItemSchema = z.object({
  kind: z.enum(["playbook", "skill", "method"]),
  name: z.string(),
  version: z.number().int(),
  title: z.string(),
  /** When to use it: Pi reads this to decide which skill to load. */
  description: z.string(),
  body: z.string(),
  allowedTools: z.array(z.string()),
});

/**
 * Everything a run is given. Flashadz builds it from D1; the runner writes it to a read-only folder and
 * loads it. Nothing else reaches the agent, so what a run knew is always on record (runs.manifest_json).
 */
export const RunManifestSchema = z.object({
  v: z.literal(1),
  runId: z.string(),
  kind: z.string(),
  accountId: z.string(),
  model: z.string(),
  product: z.object({
    id: z.string(),
    name: z.string(),
    profile: z.record(z.string(), z.unknown()).nullable(),
    material: z.string().default(""),
    /** The website the owner gave when adding the product. */
    website: z.string().nullable().default(null),
  }),
  goal: z
    .object({
      id: z.string(),
      state: z.string(),
      version: z.number().int(),
      /** The setup revision this run saw; a plan records it so a later setup edit is never lost. */
      setupRev: z.number().int(),
      config: GoalConfigSchema,
      plan: PlanSchema.nullable(),
    })
    .nullable(),
  library: z.array(ManifestItemSchema),
  lessons: z.array(z.object({ code: z.string(), claim: z.string(), strength: z.string(), learnedIn: z.string().nullable() })),
  kit: z.object({
    tools: z.array(z.string()),
    limits: z.record(z.string(), z.number().int().nonnegative()),
    capMicros: z.number().int().positive(),
  }),
  readiness: z.array(z.object({ id: z.string(), label: z.string() })),
  /** The task in plain words: the run kind's instruction, plus the owner's message for a chat run. */
  task: z.string(),
  /** For a chat run: the conversation so far in this chat or thread, oldest first. */
  conversation: z.array(z.object({ role: z.string(), text: z.string(), at: z.string() })).default([]),
  today: z.string(),
  /** What the model allows every provider to take and give back, from OpenRouter; null when it could not be read. */
  modelLimits: z.object({ contextWindow: z.number().int().positive(), maxTokens: z.number().int().positive() }).nullable().default(null),
});
export type RunManifest = z.infer<typeof RunManifestSchema>;
